import { Button, Input, NumberInput, Select } from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconClipboardList,
  IconFileText,
  IconLayoutGrid,
  IconMapPin,
  IconPackage,
  IconStack,
  IconTruck,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

function EditStockRequestCreatedListPage() {
  const { t } = useTranslation();
  const [stockRequestCreatedData, setStockRequestCreatedData] = useState<any>({
    company: localStorage.getItem("companyId"),
    name: "",
    remarks: "",
    deliveryPerson: "",
    warehouseStaffId: null,
  });
  const [editStockRequestCreatedData, setEditStockRequestCreatedData] =
    useState<any>({});
  const [tempProductList, setTempProductList] = useState<any[]>([]);
  const [tempProductData, setTempProductData] = useState<any[]>([]);
  const [tempProductInput, setTempProductInput] = useState<any[]>([]);
  const [tempWarehouseData, setTempWarehouseData] = useState<any[]>([]);
  const [tempWarehouseInput, setTempWarehouseInput] = useState<any[]>([]);
  const [tempAreaInput, setTempAreaInput] = useState<any[]>([]);
  const [tempStorageLocationInput, setTempStorageLocationInput] = useState<
    any[]
  >([]);
  const [tempAmount, setTempAmount] = useState<any[]>([]);
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  const [warehouseStaffInput, setWarehouseStaffInput] = useState("");
  const [disable, setDisable] = useState<any[]>([]);
  const [warehouseMap, setWarehouseMap] = useState<any>({});

  const [deleteProductList, setDeleteProductList] = useState<any[]>([]);
  const [productError, setProductError] = useState<boolean[]>([]);
  const [storageLocationError, setStorageLocationError] = useState<boolean[]>(
    []
  );

  const { id } = useParams();

  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();

  const parseId = (value: string) =>
    typeof value === "string"
      ? value.match(/ID:\s*([a-zA-Z0-9-]+)/)?.[1] ?? null
      : null;

  const isProductInputValid = (value: string) => {
    const productId = parseId(value);
    return !!productId && tempProductData.some((p) => p?.id == productId);
  };

  const isStorageLocationInputValid = (value: string) => {
    const locationId = parseId(value);
    return (
      !!locationId &&
      tempWarehouseData.some((w) =>
        (w?.storageLocation ?? []).some((l: any) => l?.id == locationId)
      )
    );
  };

  const validateProductInput = (index: number) => {
    setProductError((prev) => {
      const updated = [...prev];
      const value = tempProductInput[index];
      updated[index] = !!value && !isProductInputValid(value);
      return updated;
    });
  };

  const validateStorageLocationInput = (index: number) => {
    setStorageLocationError((prev) => {
      const updated = [...prev];
      const value = tempStorageLocationInput[index];
      updated[index] = !!value && !isStorageLocationInputValid(value);
      return updated;
    });
  };

  const handleSubmit = async (
    editStockRequestCreatedData: any,
    tempProductList: any[],
    deleteProductList: any[]
  ) => {
    try {
      if (Object.keys(editStockRequestCreatedData)?.length) {
        const { name, remarks, deliveryPerson, warehouseStaffId } =
          editStockRequestCreatedData;
        const dataToUpdate = {
          id: id!,
          ...(name !== undefined && { name }),
          ...(remarks !== undefined && { remarks }),
          ...(deliveryPerson !== undefined && { deliveryPerson }),
          ...(warehouseStaffId !== undefined && { warehouseStaffId }),
        };
        await single(orpcClient.stockRequest.update([dataToUpdate]));
      }

      if (deleteProductList?.length) {
        await allOrThrow(
          orpcClient.stockRequestProduct.delete(
            deleteProductList.map((productId) => ({ id: productId }))
          )
        );
      }

      let editTempProductList: any[] = [];

      let newTempProductList: any[] = [];

      tempProductList?.map((t) => {
        if (t?.id) {
          editTempProductList.push(t);
        } else {
          newTempProductList.push(t);
        }
      });

      if (editTempProductList?.length) {
        await allOrThrow(
          orpcClient.stockRequestProduct.update(
            editTempProductList.map((data) => ({
              id: data?.id,
              stockRequestId: id!,
              amount: data?.amount,
            }))
          )
        );
      }

      if (newTempProductList?.length) {
        await allOrThrow(
          orpcClient.stockRequestProduct.create(
            newTempProductList.map((data) => ({
              amount: data?.amount,
              productId: data?.product?.id,
              stockRequestId: id!,
              storageLocationId: data?.storageLocation,
            }))
          )
        );
      }
    } catch (error) {
      console.error("api error", error);
      return;
    }
    navigate(`/hrMainPage/stockRequestCreated/${id}`);
  };

  const handleDataChange = (name: string, value: any) => {
    setStockRequestCreatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setEditStockRequestCreatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeWarehouseStaff = (value: string, employeeData: any[]) => {
    setWarehouseStaffInput(value);
    const idNumber = parseId(value);
    const selectedData = employeeData.find((e: any) => e?.id == idNumber);
    handleDataChange("warehouseStaffId", selectedData?.id ?? null);
  };

  const handleDeleteProductList = (index: number, id?: string) => {
    const updatedProductList = [...tempProductList];

    updatedProductList.splice(index, 1);

    setTempProductInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempWarehouseInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAreaInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempStorageLocationInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAmount((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setDisable((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setProductError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setStorageLocationError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempProductList(updatedProductList);

    //新增的不用call delete API
    if (id) {
      setDeleteProductList((prev) => [...prev, id]);
    }
  };

  const handleOnOptionSubmit = (
    type: string,
    e: string,
    data: any[],
    index: number
  ) => {
    let selectedData: any;
    if (type === "product") {
      let match = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let idNumber = match ? match[1] : null;
      selectedData = data.filter((e: any) => e?.id == idNumber)[0];
      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = { ...updatedList[index], [type]: selectedData };
        return updatedList;
      });
      setProductError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    } else {
      let idBehindMatch = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let documentID = idBehindMatch ? idBehindMatch[1].trim() : null;

      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = {
          ...updatedList[index],
          storageLocation: documentID,
        };
        return updatedList;
      });
      setStorageLocationError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  };

  const handleAddProductList = (productList: any[]) => {
    const updatedProductList = [...productList, []];

    setTempProductList(updatedProductList);
    setDisable((prev) => {
      return [...prev, false];
    });
  };

  const handleDisplaySelectMembers = (data: any[]) => {
    return data?.map((t) => ({
      value: t?.id,
      label: t?.name,
    }));
  };
  const handleDisplaySelectWarehouse = (data: any[]) => {
    return data?.map((t) => ({ value: t?.id, label: t?.name }));
  };

  const handleDisplaySelectArea = (
    warehouseId: string,
    warehouseData: any[]
  ) => {
    if (!warehouseId) return [];

    const selectedData = warehouseData.find((e: any) => e?.id == warehouseId);

    return (selectedData?.areas ?? []).map((area: any) => ({
      value: area?.id,
      label: area?.name,
    }));
  };

  const handleDisplaySelectStorageLocation = (
    warehouseId: string,
    areaId: string,
    warehouseData: any[]
  ) => {
    if (!warehouseId || !areaId) return [];

    const selectedWarehouse = warehouseData.find(
      (e: any) => e?.id == warehouseId
    );
    const selectedArea = (selectedWarehouse?.areas ?? []).find(
      (a: any) => a?.id == areaId
    );

    return (selectedArea?.storageLocation ?? []).map((location: any) => ({
      value: location?.id,
      label: location?.name,
    }));
  };

  const { name, remarks, deliveryPerson } = stockRequestCreatedData || {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await single(orpcClient.stockRequest.read([{ id: id! }]));

        setStockRequestCreatedData(result);

        const productList = result?.stockRequestProduct || [];

        const warehouses = await orpcClient.warehouse.list({});
        const warehouseDataMap: any = {};
        const areaDataMap: any = {};
        await Promise.all(
          (warehouses ?? []).map(async (warehouse) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            (areas ?? []).forEach((area) => {
              if (area?.id) {
                warehouseDataMap[area.id] = warehouse;
                areaDataMap[area.id] = area;
              }
            });
          })
        );
        setWarehouseMap(warehouseDataMap);

        setTempProductList(
          productList.map((item) => {
            const {
              id,
              amount,
              stockRequestId,
              productId,
              storageLocationId,
            } = item;

            return {
              id,
              amount,
              stockRequestId,
              productId,
              storageLocationId,
            };
          })
        );

        setTempProductInput(
          productList?.map((item) => item?.product?.id ?? null)
        );
        setTempWarehouseInput(
          productList?.map((item) => {
            const warehouseAreaId = item?.storageLocation?.warehouseAreaId;
            const warehouse = warehouseDataMap[warehouseAreaId];
            return warehouse?.id ?? null;
          })
        );
        setTempAreaInput(
          productList?.map((item) => {
            const warehouseAreaId = item?.storageLocation?.warehouseAreaId;
            const area = areaDataMap[warehouseAreaId];
            return area?.id ?? null;
          })
        );
        setTempStorageLocationInput(
          productList?.map((item) => item?.storageLocation?.id ?? null)
        );

        setTempAmount(productList?.map((item) => item?.amount));

        setDisable(productList?.map(() => true));
      } catch (error) {
        console.error("Error fetching stock request data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.product.list({});

        setTempProductData(result);
      } catch (error) {
        console.error("Error fetching products data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.warehouse.list({});

        const warehousesWithLocations = await Promise.all(
          (result ?? []).map(async (warehouse: any) => {
            const areas = await orpcClient.warehouseArea.list({
              warehouseId: warehouse?.id,
            });
            const storageLocation = (areas ?? []).flatMap(
              (area: any) => area?.storageLocation ?? []
            );
            return { ...warehouse, areas: areas ?? [], storageLocation };
          })
        );

        setTempWarehouseData(warehousesWithLocations);
      } catch (error) {
        console.error("Error fetching warehouses data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.employee.list({});
        setEmployeeData(result || []);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const staffId = stockRequestCreatedData?.warehouseStaffId;
    if (staffId && employeeData?.length) {
      const emp = employeeData.find((e: any) => e?.id === staffId);
      if (emp) setWarehouseStaffInput(`${emp.name} (ID: ${emp.id})`);
    }
  }, [stockRequestCreatedData?.warehouseStaffId, employeeData]);

  const fieldWrapper = (
    label: React.ReactNode,
    Icon: React.ComponentType<{ size?: number | string }> | undefined,
    children: React.ReactNode
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("stockRequest.editStockRequest") || "編輯領料單"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {name || t("stockRequest.stockRequestManagement") || "領料單管理"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="p-6">
            {fieldWrapper(
              t("stockRequest.stockRequestName") || "領料單名稱",
              IconClipboardList,
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconClipboardList size={16} />}
              />
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.productList") || "商品清單"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {tempProductList.map((item, i) => {
              return (
                <div
                  className="rounded-lg border border-gray-200 p-4"
                  key={item?.id ?? i}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">
                      #{i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteProductList(i, item?.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-500 hover:bg-red-50"
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {fieldWrapper(
                      t("stockRequest.productName") || "商品名稱",
                      IconPackage,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconPackage size={16} />}
                        data={handleDisplaySelectMembers(tempProductData)}
                        value={tempProductInput[i] || null}
                        onChange={(value) => {
                          setTempProductInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });
                          const selectedData = tempProductData.find(
                            (p) => p?.id == value
                          );
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              product: selectedData,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.warehouseLabel") || "倉庫",
                      IconBuildingWarehouse,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconBuildingWarehouse size={16} />}
                        data={handleDisplaySelectWarehouse(tempWarehouseData)}
                        value={tempWarehouseInput[i] || null}
                        onChange={(value) => {
                          setTempWarehouseInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });

                          setTempAreaInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: null,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.areaLabel") || "區",
                      IconLayoutGrid,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconLayoutGrid size={16} />}
                        data={handleDisplaySelectArea(
                          tempWarehouseInput[i],
                          tempWarehouseData
                        )}
                        value={tempAreaInput[i] || null}
                        onChange={(value) => {
                          setTempAreaInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });

                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: null,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.storageLocationLabel") || "儲位",
                      IconMapPin,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconMapPin size={16} />}
                        data={handleDisplaySelectStorageLocation(
                          tempWarehouseInput[i],
                          tempAreaInput[i],
                          tempWarehouseData
                        )}
                        value={tempStorageLocationInput[i] || null}
                        onChange={(value) => {
                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: value,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.quantity") || "數量",
                      IconStack,
                      <NumberInput
                        leftSection={<IconStack size={16} />}
                        value={tempAmount[i] || ""}
                        onChange={(e) => {
                          setTempProductList((prev) => {
                            const updatedList = [...prev];
                            updatedList[i] = { ...updatedList[i], amount: e };
                            return updatedList;
                          });

                          setTempAmount((prev) => {
                            const updated = [...prev];
                            updated[i] = e;
                            return updated;
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            <Button
              variant="default"
              fullWidth
              radius="md"
              onClick={() => handleAddProductList(tempProductList)}
            >
              {t("stockRequest.addProductRow") || "增加商品列"}
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTruck size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.deliveryInfo") || "交貨資訊"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {fieldWrapper(
                t("stockRequest.requestPerson") || "領料人員",
                IconUser,
                <Input
                  leftSection={<IconUser size={16} />}
                  value={deliveryPerson}
                  onChange={(e) =>
                    handleDataChange("deliveryPerson", e.target.value)
                  }
                />
              )}
              {fieldWrapper(
                t("stockRequest.outboundStaff") || "出庫人員",
                IconTruck,
                <Select
                  searchable
                  clearable
                  nothingFoundMessage="查無資料"
                  leftSection={<IconTruck size={16} />}
                  data={employeeData
                    ?.filter((emp) => emp?.status !== "resigned")
                    .map((emp) => ({ value: emp?.id, label: emp?.name }))}
                  value={stockRequestCreatedData?.warehouseStaffId || null}
                  onChange={(value) =>
                    handleDataChange("warehouseStaffId", value)
                  }
                />
              )}
            </div>
            {fieldWrapper(
              t("stockRequest.remarks") || "備註",
              IconFileText,
              <Input
                leftSection={<IconFileText size={16} />}
                value={remarks}
                onChange={(e) => handleDataChange("remarks", e.target.value)}
              />
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            fullWidth
            radius="md"
            onClick={() => navigate(`/hrMainPage/stockRequestCreated`)}
          >
            {t("stockRequest.cancel") || "取消"}
          </Button>

          <Button
            variant="filled"
            color="gray"
            fullWidth
            radius="md"
            onClick={() =>
              handleSubmit(
                editStockRequestCreatedData,
                tempProductList,
                deleteProductList
              )
            }
          >
            {t("stockRequest.submitEdit") || "送出編輯"}
          </Button>
        </div>
      </div>
    </div>
  );
}
export default EditStockRequestCreatedListPage;
